import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Snackbar,
} from "@mui/material";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useConfirmPage } from "./useConfirmPage";

import { ReactComponent as SquareLeft } from "../../assets/square-left.svg";
import { ReactComponent as SquareRight } from "../../assets/square-right.svg";

import FilterBar from "./components/FilterBar";
import ContractList from "./components/ContractList";
import ContractDetail from "./components/ContractDetail";
import MasterConflictModal from "./components/MasterConflictModal";

const ConfirmPage: React.FC = () => {
  const {
    isTrashView,
    setIsTrashView,
    selectedContract,
    selectedEditableContract,
    isEditing,
    hasUnsavedChanges,
    loading,
    error,
    hasLoadedOnce,
    aggregatedContracts,
    paginatedContracts,
    editableContracts,
    snackbar,
    setSnackbar,
    sourceMenuAnchorEl,
    sourceFiles,
    propertyFilter,
    setPropertyFilter,
    roomFilter,
    setRoomFilter,
    lesseeFilter,
    setLesseeFilter,
    propertyOptions,
    roomOptions,
    lesseeOptions,
    clientCurrentPage,
    setClientCurrentPage,
    displayCurrentPage,
    displayTotalPages,
    getPaginationDisplayText,
    changedContractIds,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showRestoreConfirm,
    setShowRestoreConfirm,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    pendingNavigationAction,
    setPendingNavigationAction,
    contractToAction,
    showMasterConflictModal,
    setShowMasterConflictModal,
    masterConflictData,
    setPendingContractToSave,
    handleMasterConflictConfirm,
    sortOrder,
    dataMode,
    mode,
    itemsPerPage,
    setItemsPerPage,
    setGlobalSortOrder,
    contextLoading,
    hasNextPage,
    fetchNextPage,
    pageScrollRef,
    detailContentRef,
    confirmAndNavigate,
    handleSelectItem,
    handleSearch,
    handleClearSearch,
    handleOpenSourceMenu,
    handleCloseSourceMenu,
    handleOpenFile,
    handleStartEdit,
    handleCancelEdit,
    handleSaveSingleContractChanges,
    handleDeleteDetails,
    executeDelete,
    handleRestore,
    executeRestore,
    handleNext,
    handlePrev,
    handleDetailChange,
    detailFields,
    labels,
    getEditableFieldValue,
    navigate,
  } = useConfirmPage();

  // 初回ロード時のみページ全体をローディング表示に置き換える
  if (loading && editableContracts.length === 0 && !hasLoadedOnce)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box
      ref={pageScrollRef}
      sx={{
        p: 3,
        backgroundColor: "#ECEBF0",
        borderRadius: "16px",
        boxShadow: `4px 4px 6px 0 rgba(26, 26, 26, 0.25),
                        -10px -10px 6px 0 rgba(255, 255, 255, 0.95)`,
        backdropFilter: "blur(4px)",
        border: "1px solid rgba(31, 31, 31, 0.05)",
        width: "95%",
        maxWidth: "1200px", // 最大幅を設定
        display: "flex",
        flexDirection: "column",
        mt: "2rem",
        ml: "0%",
        mb: "2rem",
        position: "relative",
        overflowY: "auto",
        overflowX: "hidden",
        maxHeight: "calc(100vh - 10rem)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontWeight: "bold", color: "rgb(194, 140, 255)" }}
        >
          データベース閲覧・編集
        </Typography>
        <Button
          variant="outlined"
          onClick={() => confirmAndNavigate(() => setIsTrashView(!isTrashView))}
          sx={{
            display: "inline-block",
            color: "rgb(194, 140, 255)",
            border: `2px solid rgb(194, 140, 255)`, // 内側の線
            borderRadius: "50px", // 角丸
            bgcolor: "white",
            px: 2, // 横のパディング (theme spacing)
            py: 0.2, // 縦のパディング
            fontWeight: "bold",
            fontSize: "1.1rem",

            // ここがポイント
            boxShadow: `
              0 0 0 5px white,
              4px 6px 10px rgba(176, 139, 248, 0.8)
            `,
            "&:hover": {
              border: `2px solid rgb(194, 140, 255)`,
              bgcolor: "rgba(194, 140, 255, 0.05)",
              boxShadow: `
                0 0 0 5px white,
                6px 9px 10px rgba(176, 139, 248, 1) // 影が少し広がり、不透明度を上げる
              `,
            }
          }}
        >
          {isTrashView ? "通常表示に戻る" : "ごみ箱を表示"}
        </Button>
      </Box>

      <FilterBar
        propertyFilter={propertyFilter}
        setPropertyFilter={setPropertyFilter}
        roomFilter={roomFilter}
        setRoomFilter={setRoomFilter}
        lesseeFilter={lesseeFilter}
        setLesseeFilter={setLesseeFilter}
        propertyOptions={propertyOptions}
        roomOptions={roomOptions}
        lesseeOptions={lesseeOptions}
        mode={mode}
        dataMode={dataMode}
        isTrashView={isTrashView}
        contextLoading={contextLoading}
        handleSearch={handleSearch}
        handleClearSearch={handleClearSearch}
        confirmAndNavigate={confirmAndNavigate}
        aggregatedContracts={aggregatedContracts}
        sortOrder={sortOrder}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        clientCurrentPage={clientCurrentPage}
        setClientCurrentPage={setClientCurrentPage}
        displayCurrentPage={displayCurrentPage}
        displayTotalPages={displayTotalPages}
        getPaginationDisplayText={getPaginationDisplayText}
        setGlobalSortOrder={setGlobalSortOrder}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
      />

      {/* 紫色の点線 */}
      <Box
        sx={{
          marginLeft: "2.5%",
          width: "95%",
          height: "0px",
          borderTop: "4.5px dotted rgb(194, 140, 255)",
          mt: 0,
          mb: 3.5,
        }}
      />

      <Grid container spacing={2}>
        {/* Left Pane */}
        <Grid item xs={12} md={4}>
          <ContractList
            paginatedContracts={paginatedContracts}
            selectedContract={selectedContract}
            isTrashView={isTrashView}
            handleSelectItem={handleSelectItem}
          />
        </Grid>

        {/* Right Pane */}
        <Grid item xs={12} md={8}>
          <ContractDetail
            selectedEditableContract={selectedEditableContract}
            selectedContract={selectedContract}
            isEditing={isEditing}
            isTrashView={isTrashView}
            loading={loading}
            hasUnsavedChanges={hasUnsavedChanges}
            aggregatedContracts={aggregatedContracts}
            changedContractIds={changedContractIds}
            sourceMenuAnchorEl={sourceMenuAnchorEl}
            sourceFiles={sourceFiles}
            detailFields={detailFields}
            labels={labels}
            getEditableFieldValue={getEditableFieldValue}
            handleDetailChange={handleDetailChange}
            handleOpenSourceMenu={handleOpenSourceMenu}
            handleCloseSourceMenu={handleCloseSourceMenu}
            handleOpenFile={handleOpenFile}
            handlePrev={handlePrev}
            handleNext={handleNext}
            handleRestore={handleRestore}
            handleSaveSingleContractChanges={handleSaveSingleContractChanges}
            handleCancelEdit={handleCancelEdit}
            handleStartEdit={handleStartEdit}
            handleDeleteDetails={handleDeleteDetails}
            confirmAndNavigate={confirmAndNavigate}
            navigate={navigate}
            detailContentRef={detailContentRef}
          />
        </Grid>
      </Grid>
            <Box
        sx={{
          display: "flex",
          pointerEvents: "none", // コンテナ自体はクリックイベントを妨げない
        }}
      >
        <Box sx={{ width: "9%", pointerEvents: "auto" }}>
          <SquareLeft />
        </Box>
        <Box sx={{ width: "9%", pointerEvents: "auto", marginLeft: "auto" }}>
          <SquareRight />
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity || "info"}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmationModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={executeDelete}
        title="データ削除の確認"
        message={`「${contractToAction?.property_name} - ${contractToAction?.room_name} - ${contractToAction?.lessee_name}」に関連する全ての契約書・重説データをごみ箱に移動します。本当によろしいですか？`}
        confirmButtonText="削除"
        themeColor="error"
      />
      <ConfirmationModal
        open={showRestoreConfirm}
        onClose={() => setShowRestoreConfirm(false)}
        onConfirm={executeRestore}
        title="データ復元の確認"
        message={`「${contractToAction?.property_name} - ${contractToAction?.room_name} - ${contractToAction?.lessee_name}」を復元します。よろしいですか？`}
        confirmButtonText="復元"
        themeColor="success"
      />
      <ConfirmationModal
        open={showUnsavedChangesModal}
        onClose={() => {
          setShowUnsavedChangesModal(false);
          setPendingNavigationAction(null);
        }}
        onConfirm={() => {
          if (pendingNavigationAction) {
            pendingNavigationAction();
          }
          setShowUnsavedChangesModal(false);
          setPendingNavigationAction(null);
        }}
        title="未保存の変更"
        message="編集中のデータが保存されていません。移動すると変更は失われますが、よろしいですか？"
        confirmButtonText="移動する"
        themeColor="warning"
      />

      {/* 既存データを使用 / 新規登録 照合結果モーダル */}
      <MasterConflictModal
        open={showMasterConflictModal}
        onClose={() => { setShowMasterConflictModal(false); setPendingContractToSave(null); }}
        onConfirm={handleMasterConflictConfirm}
        masterConflictData={masterConflictData}
      />
    </Box>
  );
};

export default ConfirmPage;
